/**
 * Edit Calendar - Club admin interface for editing existing calendars
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/api';
import { AdminPageLayout, FormInput, FormTextArea, FormSelect, FormCheckbox, FormColorPicker, Button, ConfirmDialog, LoadingState } from '../../components';
import { ErrorBanner } from '../../components/common';
import { useFormValidation } from '../../hooks/useFormValidation';

const EditCalendar = () => {
  const navigate = useNavigate();
  const { calendarId } = useParams();
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const calendarScopes = [
    { value: 'club_wide', label: 'Club-wide - Visible to all club members' },
    { value: 'team_specific', label: 'Team Specific - Visible to specific teams' },
    { value: 'program_specific', label: 'Program Specific - Visible to program participants' },
    { value: 'role_specific', label: 'Role Specific - Visible to specific roles (coaches, admins)' },
    { value: 'public', label: 'Public - Visible to everyone' },
    { value: 'personal', label: 'Personal - Visible only to owner' }
  ];

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
  ];

  const tagOptions = [
    'recreational', 'competitive', 'youth', 'adult', 'camps', 'clinics',
    'indoor', 'outdoor', 'travel', 'premier', 'development'
  ];

  const {
    formData,
    errors,
    touched,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFormData,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      name: '',
      description: '',
      calendar_scope: 'club_wide',
      season_id: '',
      is_active: true,
      color_code: '#1565C0',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      tags: []
    },
    validationSchema: (values) => {
      const newErrors = {};

      if (!values.name.trim()) {
        newErrors.name = 'Calendar name is required';
      }

      return newErrors;
    },
    onSubmit: async (values) => {
      // Only send changed fields
      const changedData = {};
      Object.keys(values).forEach(key => {
        if (JSON.stringify(values[key]) !== JSON.stringify(originalData[key])) {
          changedData[key] = values[key];
        }
      });

      if (Object.keys(changedData).length === 0) {
        navigate('/admin/calendars');
        return;
      }

      try {
        const calendarData = {
          ...changedData,
          tags: Array.isArray(changedData.tags) ? changedData.tags : originalData.tags
        };

        await apiService.updateCalendar(calendarId, calendarData);
        navigate('/admin/calendars');
      } catch (err) {
        setError(err.message || 'Failed to update calendar');
        throw err;
      }
    }
  });

  useEffect(() => {
    loadCalendar();
  }, [calendarId]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const response = await apiService.request('/api/calendars/');
      const calendar = response.calendars.find(c => c.id === calendarId);

      if (!calendar) {
        setError('Calendar not found');
        setLoading(false);
        return;
      }

      const calendarData = {
        name: calendar.name || '',
        description: calendar.description || '',
        calendar_scope: calendar.calendar_scope || 'club_wide',
        season_id: calendar.season_id || '',
        is_active: calendar.is_active !== undefined ? calendar.is_active : true,
        color_code: calendar.color_code || '#1565C0',
        timezone: calendar.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
        tags: calendar.tags || []
      };

      setOriginalData(calendarData);
      setFormData(calendarData);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load calendar');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteCalendar(calendarId);
      navigate('/admin/calendars');
    } catch (err) {
      setError(err.message || 'Failed to delete calendar');
      setShowDeleteConfirm(false);
    }
  };

  const handleTagToggle = (tag) => {
    const currentTags = formData.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    setFieldValue('tags', newTags);
  };

  if (loading) {
    return (
      <AdminPageLayout
        title="EDIT CALENDAR"
        subtitle="Loading calendar information..."
        backPath="/admin/calendars"
      >
        <LoadingState message="Loading calendar..." />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="EDIT CALENDAR"
      subtitle={`Editing: ${originalData.name}`}
      backPath="/admin/calendars"
    >
      <div className="edit-calendar-page">
        {error && (
          <ErrorBanner
            type="error"
            message={error}
            onDismiss={() => setError(null)}
          />
        )}

        <form onSubmit={handleFormSubmit} className="calendar-form">
          <div className="form-section">
            <h3>Calendar Details</h3>

            <div className="form-row">
              <FormInput
                label="Calendar Name"
                name="name"
                value={formData.name}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.name ? errors.name : ''}
                placeholder="e.g., Fall 2024 Season, Summer Camps"
                required
              />

              <FormSelect
                label="Calendar Scope"
                name="calendar_scope"
                value={formData.calendar_scope}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.calendar_scope ? errors.calendar_scope : ''}
                options={calendarScopes}
                required
              />
            </div>

            <FormTextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.description ? errors.description : ''}
              placeholder="Describe the calendar purpose and schedule"
              rows={3}
            />

            <FormInput
              label="Season (Optional)"
              name="season_id"
              value={formData.season_id}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.season_id ? errors.season_id : ''}
              placeholder="Leave blank to associate with a season later"
              help="Dates will come from the associated season"
            />
          </div>

          <div className="form-section">
            <h3>Calendar Settings</h3>

            <div className="form-row">
              <FormColorPicker
                label="Color Code"
                name="color_code"
                value={formData.color_code}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.color_code ? errors.color_code : ''}
              />

              <FormSelect
                label="Timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.timezone ? errors.timezone : ''}
                options={timezones}
              />
            </div>

            <FormCheckbox
              label="Active"
              name="is_active"
              checked={formData.is_active}
              onChange={(e) => setFieldValue('is_active', e.target.checked)}
            />
            <p className="field-hint" style={{ marginTop: '-12px', marginLeft: '28px', fontSize: '0.875rem', color: '#666' }}>
              Only active calendars are visible and can be used
            </p>
          </div>

          <div className="form-section">
            <h3>Tags</h3>
            <div className="tag-selector">
              {tagOptions.map(tag => (
                <button
                  key={tag}
                  type="button"
                  className={`tag-button ${formData.tags.includes(tag) ? 'selected' : ''}`}
                  onClick={() => handleTagToggle(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <div className="form-actions-left">
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting}
              >
                <i className="fas fa-trash"></i>
                Delete Calendar
              </Button>
            </div>
            <div className="form-actions-right">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/admin/calendars')}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Calendar"
          message={`Are you sure you want to delete the calendar "${formData.name}"? This action cannot be undone and will delete all associated events.`}
          confirmLabel="Delete"
          variant="danger"
        />
      </div>
    </AdminPageLayout>
  );
};

export default EditCalendar;
